'use client';

/**
 * The Audax Group - Homepage
 * Bold dual-pathway experience for brands and consumers
 */

import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';
import {
  Building2,
  Sparkles,
  TrendingUp,
  Users,
  Heart,
  Calendar,
  ShoppingBag,
  ArrowRight,
  Zap,
} from 'lucide-react';
import { activations, brandMetrics } from '@/lib/data';

export default function HomePage() {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Dual Pathways */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/images/generated/hero-experiential-activation-crowd.png"
            alt="Experiential activation crowd"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/60 to-background/30" />
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
            className="max-w-6xl"
          >
            <motion.div variants={fadeInUp} className="mb-8">
              <h1 className="text-6xl md:text-8xl font-black mb-6 leading-none">
                <span className="text-gradient-electric block">
                  CULTURE
                </span>
                <span className="text-gradient-sunset block">
                  CREATORS
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-foreground/90 max-w-2xl font-bold">
                Bold experiential retail activations that connect brands with
                culture and consumers with unforgettable moments.
              </p>
            </motion.div>

            {/* Dual Pathway Cards */}
            <motion.div
              variants={fadeInUp}
              className="grid md:grid-cols-2 gap-6 mt-12"
            >
              {/* Brand Pathway */}
              <Card className="group relative overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5 border-primary/50 hover:border-primary transition-all duration-300 card-hover">
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -mr-32 -mt-32 group-hover:bg-primary/20 transition-all" />
                <div className="relative p-8">
                  <Building2 className="w-12 h-12 text-primary mb-4" />
                  <h2 className="text-3xl font-black mb-3 uppercase">
                    For Brands
                  </h2>
                  <p className="text-foreground/80 mb-6 text-lg">
                    Partner with us to create revenue-driven activations that
                    amplify brand awareness and cultural impact.
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      <span className="font-bold">+58% Avg Brand Lift</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Users className="w-5 h-5 text-primary" />
                      <span className="font-bold">125K+ Total Attendees</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Heart className="w-5 h-5 text-primary" />
                      <span className="font-bold">Cultural Authenticity</span>
                    </div>
                  </div>
                  <Button
                    asChild
                    size="lg"
                    className="w-full group/btn bg-primary hover:bg-primary/90"
                  >
                    <Link href="/partnerships">
                      Partner With Us
                      <ArrowRight className="ml-2 w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </Card>

              {/* Consumer Pathway */}
              <Card className="group relative overflow-hidden bg-gradient-to-br from-secondary/20 to-secondary/5 border-secondary/50 hover:border-secondary transition-all duration-300 card-hover">
                <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/10 rounded-full blur-3xl -mr-32 -mt-32 group-hover:bg-secondary/20 transition-all" />
                <div className="relative p-8">
                  <Sparkles className="w-12 h-12 text-secondary mb-4" />
                  <h2 className="text-3xl font-black mb-3 uppercase">
                    For Fans
                  </h2>
                  <p className="text-foreground/80 mb-6 text-lg">
                    Experience unforgettable pop-up events and shop exclusive
                    World Cup 2026 Atlanta merchandise.
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-5 h-5 text-secondary" />
                      <span className="font-bold">Upcoming Events</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ShoppingBag className="w-5 h-5 text-secondary" />
                      <span className="font-bold">Exclusive Merch</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Zap className="w-5 h-5 text-secondary" />
                      <span className="font-bold">Limited Experiences</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      asChild
                      size="lg"
                      className="group/btn bg-secondary hover:bg-secondary/90"
                    >
                      <Link href="/events">
                        Events
                        <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                    <Button
                      asChild
                      size="lg"
                      variant="outline"
                      className="border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground"
                    >
                      <Link href="/shop">Shop</Link>
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Activations */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase">
              Featured <span className="text-gradient-electric">Activations</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Culture-forward experiences that drive real brand impact
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {activations.slice(0, 3).map((activation, index) => (
              <motion.div
                key={activation.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link href={`/activations/${activation.id}`}>
                  <Card className="overflow-hidden card-hover border-2 border-transparent hover:border-primary group">
                    <div className="relative h-64">
                      <Image
                        src={activation.image}
                        alt={activation.title}
                        fill
                        className="object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <p className="text-sm font-bold text-primary mb-1">
                          {activation.brand}
                        </p>
                        <h3 className="text-2xl font-black text-foreground line-clamp-2">
                          {activation.title}
                        </h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {activation.description}
                      </p>
                      {activation.metrics.brandAwarenessLift && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold">Brand Lift:</span>
                          <span className="text-2xl font-black text-gradient-electric">
                            {activation.metrics.brandAwarenessLift}
                          </span>
                        </div>
                      )}
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" variant="outline">
              <Link href="/activations">
                View All Activations
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase">
              The <span className="text-gradient-sunset">Numbers</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="text-5xl md:text-6xl font-black text-gradient-electric mb-2">
                {brandMetrics.totalActivations}+
              </div>
              <p className="text-lg font-bold text-muted-foreground">
                Activations
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="text-5xl md:text-6xl font-black text-gradient-electric mb-2">
                {(brandMetrics.totalAttendees / 1000).toFixed(0)}K+
              </div>
              <p className="text-lg font-bold text-muted-foreground">
                Attendees
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="text-5xl md:text-6xl font-black text-gradient-sunset mb-2">
                {brandMetrics.avgBrandLift}
              </div>
              <p className="text-lg font-bold text-muted-foreground">
                Avg Brand Lift
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="text-5xl md:text-6xl font-black text-gradient-sunset mb-2">
                {brandMetrics.socialReach}
              </div>
              <p className="text-lg font-bold text-muted-foreground">
                Social Reach
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <Card className="relative overflow-hidden bg-gradient-electric p-12 md:p-16 text-center">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[url('/images/generated/hero-experiential-activation-crowd.png')] bg-cover bg-center" />
            </div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-black mb-6 text-white uppercase">
                Ready to Create Culture?
              </h2>
              <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
                Let's build an experiential activation that drives real brand
                impact and creates unforgettable moments.
              </p>
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="text-lg px-8 h-14 shadow-2xl"
              >
                <Link href="/partnerships">
                  Start a Partnership
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
